import { createTheme } from "@mui/material/styles";
import { enUS, esES } from "@mui/x-date-pickers/locales";
import { createOutpaveTheme } from "@outpave/ds";
import type { BrandIdentity, ThemePresetName } from "@outpave/ds";
import { dateLocaleFor } from "utils/dayjs";
import { dsIcons } from "./dsIcons";

/**
 * THE breakpoints. Every width decision in the app resolves here, so tuning a
 * tier is one edit rather than a search.
 *
 * They are MUI's own defaults, stated explicitly rather than inherited: the
 * package theme sets none, and leaving them implicit is how the app ended up
 * with five disagreeing sources (a 900px hook, a 1039.95px query, a 768-1023
 * range, dead 375/834/1440 CSS variables, and Tailwind's screens). Writing
 * them down is what makes them ours to change.
 *
 * This module is now the only source: Tailwind and its mirrored `screens` were
 * removed in OUT-1174.
 *
 * Never hand-write a media query against these. `theme.breakpoints.down("md")`
 * emits the boundary MUI expects, including the fractional edge that stops
 * `up` and `down` overlapping at the same pixel.
 */
export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const;

const PICKER_WORDS = { en: enUS, es: esES };

/**
 * The package's date pickers read their words and month names from the theme,
 * because each one wraps its own LocalizationProvider.
 */
const pickerLocale = (language: string | undefined) => {
  const locale = dateLocaleFor(language);
  return [
    PICKER_WORDS[locale],
    {
      components: {
        MuiLocalizationProvider: { defaultProps: { adapterLocale: locale } },
      },
    },
  ];
};

/** The app's only `createTheme` call. */
export const buildOutpaveTheme = (
  identity?: BrandIdentity,
  preset: ThemePresetName = "house",
  language?: string,
) =>
  createTheme(
    createOutpaveTheme({
      ...(identity ? { identity } : {}),
      icons: dsIcons,
      preset,
    }),
    { breakpoints: { values: { ...BREAKPOINTS } } },
    ...pickerLocale(language),
  );

/** Base-brand instance for boot, tests, and identity-less contexts. */
export const outpaveTheme = buildOutpaveTheme();
